/**
 * Typographie des réponses, selon leur langue (repris de Studio, `nextjs/src/components/chatbot/typography.ts`).
 *
 * En français, une espace insécable à l'intérieur des guillemets et avant « : » et « % », et une espace
 * fine insécable avant « ; », « ! » et « ? » : la ponctuation ne commence jamais une ligne. Les autres
 * langues restent telles quelles (l'anglais n'a pas d'espace avant la ponctuation).
 */

export type Language = "fr" | "en";

const NBSP = "\u00a0";
const NARROW_NBSP = "\u202f";

const FRENCH_WORDS = new Set(["le", "la", "les", "des", "une", "est", "que", "qui", "dans", "pour", "pas", "et", "du", "au", "avec", "sur", "ne", "ce", "cette", "il", "elle", "sont", "plus", "mais", "ou", "par"]);
const ENGLISH_WORDS = new Set(["the", "and", "of", "to", "is", "in", "that", "with", "for", "are", "this", "was", "it", "as", "on", "by", "not", "but", "his", "her", "which"]);

/** "fr" sauf si le texte est nettement anglais : le français est la langue du produit, et les textes courts ou en cours restent en français. */
export function detectLanguage(text: string): Language {
  let french = 0;
  let english = 0;
  for (const word of text.toLowerCase().match(/\p{L}+/gu) ?? []) {
    if (FRENCH_WORDS.has(word)) french++;
    if (ENGLISH_WORDS.has(word)) english++;
  }
  return english >= 3 && english > french ? "en" : "fr";
}

// Les adresses ne sont pas touchées : « https://site.fr/a?b=1 » reste d'un seul tenant.
const ADDRESS = /(\S*:\/\/\S*|www\.\S+)/;
const SPACES = "[ \\t\\u00A0\\u202F]";

function frenchPiece(text: string): string {
  return (
    text
      // Espace avant ; ! ? (présente, ou absente après la fin d'un mot) → espace fine insécable.
      .replace(new RegExp(`${SPACES}+([;!?])`, "g"), `${NARROW_NBSP}$1`)
      .replace(/([\p{L}\p{N})\]»"”’'])([;!?])(?=\s|$)/gu, `$1${NARROW_NBSP}$2`)
      // Deux-points : espace insécable avant, quand il introduit quelque chose (pas « 10:30 »).
      .replace(new RegExp(`${SPACES}+:(?=\\s|$)`, "g"), `${NBSP}:`)
      .replace(/([\p{L})\]»"”’'])(:)(?=\s|$)/gu, `$1${NBSP}$2`)
      // 50 % : une espace déjà là devient insécable.
      .replace(new RegExp(`(\\d)${SPACES}+%`, "g"), `$1${NBSP}%`)
      // Guillemets : espace insécable à l'intérieur, que l'auteur l'ait tapée ou non.
      .replace(new RegExp(`«${SPACES}*`, "g"), `«${NBSP}`)
      .replace(new RegExp(`${SPACES}*»`, "g"), `${NBSP}»`)
  );
}

export function frenchTypography(text: string): string {
  return text
    .split(ADDRESS)
    .map((part, index) => (index % 2 ? part : frenchPiece(part)))
    .join("");
}

/** La typographie de `text` dans `language` (inchangé pour une langue sans règles). */
export function typography(text: string, language: Language): string {
  return language === "fr" ? frenchTypography(text) : text;
}
